import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import Header from './Header';
import Footer from './Footer';
import BottomNavigationBar from './BottomNavigationBar';
import ProfileSidebar from './ProfileSidebar';
import ProfileEditModal from './ProfileEditModal';
import SkillsPopup from './SkillsPopup';
import ProfileSetupTimeline from './ProfileSetupTimeline';
import ApiService from '../services/api';
import { getApiUrl } from '../config/api';
import './ProfileStatusPage.css';

const ProfileStatusPage = () => {
  const { user, isAuthenticated, isLoading: authLoading, updateUser } = useAuth();
  const navigate = useNavigate();
  const [isProfileSidebarOpen, setIsProfileSidebarOpen] = useState(false);
  
  const [completionStatus, setCompletionStatus] = useState(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showSkillsPopup, setShowSkillsPopup] = useState(false);
  const [userSkills, setUserSkills] = useState([]);
  const [skillsLoading, setSkillsLoading] = useState(false);
  const [userProjects, setUserProjects] = useState([]);
  const [projectsLoading, setProjectsLoading] = useState(false);

  // Calculate profile completion percentage
  const profileCompletion = completionStatus ? completionStatus.percentage : 0;

  useEffect(() => {
    if (user && isAuthenticated) {
      fetchCompletionStatus();
      fetchUserSkills();
      fetchUserProjects();
    }
  }, [user, isAuthenticated]);

  const fetchCompletionStatus = async () => {
    try {
      const response = await ApiService.get('/profile/completion-status');
      if (response.success) {
        setCompletionStatus(response.data);
      }
    } catch (error) {
      console.error('Error fetching completion status:', error);
    }
  };

  const fetchUserSkills = async () => {
    try {
      setSkillsLoading(true);
      const response = await ApiService.get('/user-skills');
      if (response.success) {
        setUserSkills(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching skills:', error);
    } finally {
      setSkillsLoading(false);
    }
  };

  const fetchUserProjects = async () => {
    try {
      setProjectsLoading(true);
      const response = await ApiService.get('/personal-projects');
      if (response.success) {
        setUserProjects(response.data || []);
      }
    } catch (error) {
      console.error('Error fetching projects:', error);
    } finally {
      setProjectsLoading(false);
    }
  };

  const handleSkillsSave = async (skills) => {
    try {
      // Refresh skills after save
      await fetchUserSkills();
      await fetchCompletionStatus();
      setShowSkillsPopup(false);
    } catch (error) {
      console.error('Error saving skills:', error);
    }
  };

  const handleModalSuccess = async () => {
    // Refresh completion status after modal updates
    await fetchCompletionStatus();
    setShowEditModal(false);
  };

  const handleProfileButtonClick = () => {
    setIsProfileSidebarOpen(true);
  };

  const handleProfileClick = () => {
    navigate('/profile');
  };

  const handleAccountSettingsClick = () => {
    navigate('/settings');
  };

  const handleProfileStatusClick = () => {
    // Already on profile status page
    setIsProfileSidebarOpen(false);
  };

  const handleHomeClick = () => {
    navigate('/');
  };

  const handleCategoriesClick = () => {
    navigate('/categories');
  };

  const handleHowItWorksClick = () => {
    navigate('/#how-it-works');
  };

  const handleForumClick = () => {
    navigate('/forum');
  };

  const handleJobsClick = () => {
    navigate('/vacancies');
  };

  const handleMessagesClick = () => {
    // Placeholder
  };

  const handleNotificationsClick = () => {
    // Handled by NotificationSystem component
  };

  if (authLoading) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading...
        </div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  if (!user) {
    return (
      <div className="App">
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          fontSize: '18px',
          color: '#666'
        }}>
          Loading user data...
        </div>
      </div>
    );
  }

  return (
    <div className="App profile-status-page">
      {/* Header - visible on desktop/tablet, hidden on mobile */}
      <div className="profile-status-page-header">
        <Header 
          onLoginClick={() => navigate('/login')}
          onSignupClick={() => navigate('/signup')}
          isLoggedIn={isAuthenticated}
          user={user}
          onHomeClick={handleHomeClick}
          onCategoriesClick={handleCategoriesClick}
          onHowItWorksClick={handleHowItWorksClick}
          onProfileClick={handleProfileClick}
          onAccountSettingsClick={handleAccountSettingsClick}
          onForumClick={handleForumClick}
          currentPage="profile-status"
        />
      </div>

      <div className="profile-status-content">
        <div className="profile-status-container">
          <div className="profile-status-header">
            <h1>Profile Status</h1>
          </div>

          <div className="profile-status-main">
            <ProfileSetupTimeline
              user={user}
              completionStatus={completionStatus}
              profileCompletion={profileCompletion}
              userSkills={userSkills}
              userProjects={userProjects}
              setShowEditModal={setShowEditModal}
              setShowSkillsPopup={setShowSkillsPopup}
              setShowProjectPopup={() => navigate('/profile')}
              onAccountSettingsClick={handleAccountSettingsClick}
            />
          </div>
        </div>
      </div>

      <Footer />

      {/* Bottom Navigation Bar */}
      <BottomNavigationBar
        onHomeClick={handleHomeClick}
        onLoginClick={() => navigate('/login')}
        onSignupClick={() => navigate('/signup')}
        onProfileClick={handleProfileButtonClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onJobsClick={handleJobsClick}
        onMessagesClick={handleMessagesClick}
        onNotificationsClick={handleNotificationsClick}
        onForumClick={handleForumClick}
      />

      {/* Profile Sidebar */}
      <ProfileSidebar
        isOpen={isProfileSidebarOpen}
        onClose={() => setIsProfileSidebarOpen(false)}
        onProfileClick={handleProfileClick}
        onAccountSettingsClick={handleAccountSettingsClick}
        onProfileStatusClick={handleProfileStatusClick}
      />

      {/* Profile Edit Modal */}
      <ProfileEditModal
        isOpen={showEditModal}
        onClose={() => setShowEditModal(false)}
        user={user}
        updateUser={updateUser}
        onSuccess={handleModalSuccess}
      />

      {/* Skills Popup */}
      <SkillsPopup
        isOpen={showSkillsPopup}
        onClose={() => setShowSkillsPopup(false)}
        onSave={handleSkillsSave}
        userSkills={userSkills}
      />
    </div>
  );
};

export default ProfileStatusPage;

